import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Button, Input, Table, Modal, LoadingSpinner, DoubleScrollbar } from '../../components/UI';
import SearchableSelect from '../../components/UI/SearchableSelect';
import { apiService } from '../../services/apiService';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';
import { useAuth } from '../../contexts/AuthContext';
import { Edit, Trash2, Wrench, Eye, RefreshCw, ChevronRight, ChevronDown, Calendar, User, Briefcase, FileText, Search } from 'lucide-react';
import toast from 'react-hot-toast';

interface WarrantyItem {
  _id: string;
  customer: {
    _id: string;
    name: string;
    email?: string;
    phone?: string;
  };
  project: {
    _id: string;
    name: string;
    address?: string;
  };
  workOrder: {
    number: string;
    date: string;
  };
  deliveryInvoice?: {
    _id: string;
    invoiceNumber: string;
    deliveryDate?: string;
    date?: string;
    createdAt?: string;
  };
  product: {
    _id: string;
    name: string;
    brand?: string;
    model?: string;
    sku?: string;
    customFields?: any;
  };
  brand?: string;
  model?: string;
  serialNo?: string;
  compressorType?: string;
  notes?: string;
  quantity?: number;
  unit?: string;
  serviceWarrantyYears?: number;
  sparePartsWarrantyYears?: number;
  compressorWarrantyYears?: number;
  warrantyYears?: number;
  warrantyStartDate: string;
  serviceWarrantyEndDate?: string;
  sparePartsWarrantyEndDate?: string;
  compressorWarrantyEndDate?: string;
  warrantyEndDate?: string;
  serviceDaysRemaining?: number;
  sparePartsDaysRemaining?: number;
  compressorDaysRemaining?: number;
  daysRemaining?: number;
  status: 'active' | 'expired' | 'expiring_soon';
  createdAt: string;
  updatedAt: string;
}

interface WarrantyFormData {
  deliveryInvoiceId: string;
  customerId: string;
  projectId: string;
  workOrder: {
    number: string;
    date: string;
  };
  productId: string;
  serviceWarrantyYears?: number;
  sparePartsWarrantyYears?: number;
  compressorWarrantyYears?: number;
  warrantyYears?: number;
  warrantyStartDate: string;
}

interface DeliveryInvoiceItem {
  _id: string;
  invoiceNumber: string;
  type: string;
  status: string;
  customer: {
    name: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  project?: {
    _id: string;
    name: string;
    address?: string;
  };
  workOrder?: {
    number?: string;
    date?: string;
  };
  deliveryAddress?: string;
  dispatchedThrough?: {
    name: string;
    phone?: string;
  };
  items: Array<{
    product?: {
      _id: string;
      name: string;
      description?: string;
      unit?: string;
    };
    name?: string;
    brand?: string;
    model?: string;
    sku?: string;
    type?: string;
    compressorType?: string;
    countryOfOrigin?: string;
    countryOfAssembly?: string;
    details?: string;
    description?: string;
    capacity?: string;
    serialNo?: string;
    partNo?: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    totalPrice: number;
  }>;
  subtotal: number;
  discount: number;
  total: number;
  createdAt: string;
  date?: string;
}

interface WarrantyService {
  _id: string;
  warrantyId: string;
  serviceDate: string;
  serviceType: 'maintenance' | 'repair' | 'inspection' | 'emergency' | 'regular_servicing' | 'master_servicing' | 'other';
  workDetails?: string;
  performer?: {
    _id: string;
    name: string;
    designation?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ServiceFormData {
  warrantyId: string;
  serviceDate: string;
  serviceType: 'maintenance' | 'repair' | 'inspection' | 'emergency' | 'regular_servicing' | 'master_servicing' | 'other';
  workDetails: string;
  performer: string;
}

const WarrantyPage: React.FC = () => {
  const { settings } = useSettings();
  const { user, token, isAdmin } = useAuth();
  const [warranties, setWarranties] = useState<WarrantyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingWarranty, setEditingWarranty] = useState<WarrantyItem | null>(null);
  const [selectedWarrantyForView, setSelectedWarrantyForView] = useState<WarrantyItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState<string>('');
  const [filterProjectId, setFilterProjectId] = useState<string>('');
  const [filterInvoiceNumber, setFilterInvoiceNumber] = useState<string>('');
  const [filterInvoiceDate, setFilterInvoiceDate] = useState<string>('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [deliveryInvoices, setDeliveryInvoices] = useState<DeliveryInvoiceItem[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [selectedDeliveryInvoice, setSelectedDeliveryInvoice] = useState<DeliveryInvoiceItem | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Array<{
    productId: string;
    productName: string;
    quantity: number;
    serviceWarrantyYears?: number;
    sparePartsWarrantyYears?: number;
    compressorWarrantyYears?: number;
    warrantyYears?: number;
    warrantyStartDate: string;
  }>>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  // Memoized options for filters
  const customerOptions = useMemo(() => 
    customers.map(c => ({ value: c._id, label: c.name })),
  [customers]);

  const projectOptions = useMemo(() => 
    projects
      .filter(p => !filterCustomerId || p.customerName === customers.find(c => c._id === filterCustomerId)?.name)
      .map(p => ({ value: p._id, label: p.name })),
  [projects, filterCustomerId, customers]);

  const employeeOptions = useMemo(() => 
    employees.map(e => ({ value: e._id, label: `${e.name}${e.designation ? ` (${e.designation})` : ''}` })),
  [employees]);

  const invoiceOptions = useMemo(() => {
    const uniqueInvoices = Array.from(new Set(warranties.map(w => w.deliveryInvoice?.invoiceNumber).filter(Boolean)));
    return uniqueInvoices.map(inv => ({ value: inv!, label: inv! }));
  }, [warranties]);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'expiring_soon', label: 'Expiring Soon' },
    { value: 'expired', label: 'Expired' }
  ];

  // Service-related state
  const [services, setServices] = useState<WarrantyService[]>([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedWarrantyForService, setSelectedWarrantyForService] = useState<WarrantyItem | null>(null);
  const [serviceCounts, setServiceCounts] = useState<{[key: string]: number}>({});
  const [showServiceDetailsModal, setShowServiceDetailsModal] = useState(false);
  const [selectedWarrantyForDetails, setSelectedWarrantyForDetails] = useState<WarrantyItem | null>(null);
  const [serviceFormData, setServiceFormData] = useState<ServiceFormData>({
    warrantyId: '',
    serviceDate: new Date().toISOString().split('T')[0],
    serviceType: 'maintenance',
    workDetails: '',
    performer: ''
  });

  const [formData, setFormData] = useState<WarrantyFormData>({
    deliveryInvoiceId: '',
    customerId: '',
    projectId: '',
    workOrder: { number: '', date: '' },
    productId: '',
    serviceWarrantyYears: 0,
    sparePartsWarrantyYears: 0,
    compressorWarrantyYears: 0,
    warrantyYears: 0,
    warrantyStartDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchWarranties(),
          fetchCustomers(),
          fetchProjects(),
          fetchEmployees(),
          fetchProducts(),
          fetchAllDeliveryInvoices()
        ]);
      } catch (error) {
        console.error('Error fetching warranty data:', error);
        toast.error('Failed to fetch warranty data');
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await apiService.get('/employees');
      setEmployees(response.data.employees || response.data || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      setEmployees([]);
    }
  };

  // Fetch service counts for all warranties
  const fetchServiceCounts = useCallback(async () => {
    try {
      if (!warranties || warranties.length === 0) {
        console.log('No warranties to fetch counts for');
        return;
      }
      
      console.log('Fetching service counts for warranties:', warranties.length);
      const counts: {[key: string]: number} = {};
      
      // Use Promise.all to fetch all counts in parallel for better performance
      const countPromises = warranties.map(async (warranty) => {
        try {
          const response = await apiService.get(`/warranties/${warranty._id}/services/count`);
          console.log(`Service count for warranty ${warranty._id}:`, response.data);
          return { id: warranty._id, count: response.data.count || 0 };
        } catch (error) {
          console.error(`Error fetching count for warranty ${warranty._id}:`, error);
          // Fallback: try to get services and count them
          try {
            const servicesResponse = await apiService.get(`/warranties/${warranty._id}/services`);
            const count = servicesResponse.data?.length || 0;
            console.log(`Fallback count for warranty ${warranty._id}:`, count);
            return { id: warranty._id, count };
          } catch (fallbackError) {
            console.error(`Fallback error for warranty ${warranty._id}:`, fallbackError);
            return { id: warranty._id, count: 0 };
          }
        }
      });
      
      const results = await Promise.all(countPromises);
      
      // Convert results to counts object
      results.forEach(result => {
        counts[result.id] = result.count;
      });
      
      console.log('Final service counts:', counts);
      setServiceCounts(counts);
    } catch (error) {
      console.error('Error fetching service counts:', error);
    }
  }, [warranties]);

  // Fetch service counts after warranties are loaded
  useEffect(() => {
    if (warranties.length > 0) {
      fetchServiceCounts();
    }
  }, [warranties, fetchServiceCounts]);

  const fetchWarranties = async () => {
    try {
      const response = await apiService.get('/warranties');
      setWarranties(response.data || []);
    } catch (error) {
      console.error('Error fetching warranties:', error);
      toast.error('Failed to fetch warranties');
    }
  };

  // Soft refresh: refetch warranties when header clears cache
  useEffect(() => {
    const onRefresh = () => {
      apiService.invalidateWarrantiesCache();
      fetchWarranties();
    };
    window.addEventListener('app:refresh', onRefresh as any);
    return () => window.removeEventListener('app:refresh', onRefresh as any);
  }, []);

  const fetchCustomers = async () => {
    try {
      console.log('Fetching customers...');
      const response = await apiService.get('/customers');
      console.log('Customers response:', response.data);
      setCustomers(response.data.customers || response.data || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setCustomers([]);
    }
  };

  const fetchProjects = async () => {
    try {
      console.log('Fetching projects...');
      const response = await apiService.get('/projects');
      console.log('Projects response:', response.data);
      setProjects(response.data.projects || response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setProjects([]);
    }
  };

  const fetchAllDeliveryInvoices = async () => {
    try {
      console.log('Fetching all delivery invoices...');
      const response = await apiService.get('/invoices?type=delivery');
      console.log('Delivery invoices response:', response.data);
      setDeliveryInvoices(response.data.invoices || response.data || []);
    } catch (error) {
      console.error('Failed to fetch delivery invoices:', error);
      setDeliveryInvoices([]);
    }
  };

  const fetchWorkOrders = async (projectId: string) => {
    try {
      const response = await apiService.get(`/projects/${projectId}/work-orders`);
      setWorkOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch work orders:', error);
    }
  };

  const fetchDeliveryInvoices = async (workOrderId: string) => {
    try {
      const response = await apiService.get(`/invoices?type=delivery&workOrder=${workOrderId}`);
      setDeliveryInvoices(response.data.invoices || response.data || []);
    } catch (error) {
      console.error('Failed to fetch delivery invoices:', error);
      setDeliveryInvoices([]);
    }
  };

  const fetchProducts = async () => {
    try {
      console.log('Fetching products...');
      const response = await apiService.get('/products');
      console.log('Products response:', response.data);
      setProducts(response.data.products || response.data || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setProducts([]);
    }
  };

  const handleDeliveryInvoiceChange = (deliveryInvoiceId: string) => {
    const selectedInvoice = deliveryInvoices.find(invoice => invoice._id === deliveryInvoiceId);
    setSelectedDeliveryInvoice(selectedInvoice || null);
    
    if (selectedInvoice) {
      console.log('Selected invoice:', selectedInvoice);
      console.log('Available customers:', customers);
      console.log('Available projects:', projects);
      
      // For now, we'll need to find the customer and project by name since the invoice doesn't contain IDs
      // This is a temporary solution - ideally the invoice should contain the IDs
      const customer = customers.find(c => c.name === selectedInvoice.customer.name);
      const project = projects.find(p => p.name === selectedInvoice.project?.name);
      
      console.log('Found customer:', customer);
      console.log('Found project:', project);
      
      setFormData({
        ...formData,
        deliveryInvoiceId,
        customerId: customer?._id || '',
        projectId: project?._id || '',
        workOrder: {
          number: selectedInvoice.workOrder?.number || '',
          date: selectedInvoice.workOrder?.date || ''
        },
      });
      
      // Get the delivery invoice date as default start date
      const deliveryDate = selectedInvoice.date || selectedInvoice.createdAt || new Date().toISOString();
      const defaultStartDate = new Date(deliveryDate).toISOString().split('T')[0];
      
      // Set products from the delivery invoice (but don't auto-select them)
      const invoiceProducts = selectedInvoice.items.map(item => ({
        productId: item.product?._id || '',
        productName: `${item.product?.name || item.name || ''} - ${item.brand || ''} ${item.model || ''}`,
        quantity: item.quantity,
        serviceWarrantyYears: (item as any).warrantyDetails?.serviceWarranty || 0,
        sparePartsWarrantyYears: (item as any).warrantyDetails?.sparePartsWarranty || 0,
        compressorWarrantyYears: (item as any).warrantyDetails?.compressorWarranty || 0,
        warrantyYears: (item as any).warrantyDetails?.warranty || 0,
        warrantyStartDate: defaultStartDate,
      }));
      setSelectedProducts(invoiceProducts);
      setSelectedProductIds(new Set()); // Reset selected products
      
      console.log('Set form data:', {
        deliveryInvoiceId,
        customerId: customer?._id || '',
        projectId: project?._id || '',
        workOrder: { number: '', date: '' },
      });
      console.log('Set selected products:', invoiceProducts);
    } else {
      setFormData({
        ...formData,
        deliveryInvoiceId,
        customerId: '',
        projectId: '',
        workOrder: { number: '', date: '' },
      });
      setSelectedProducts([]);
    }
  };

  const handleAddWarranty = async () => {
    try {
      // Debug: Log the data being sent
      console.log('Form data:', formData);
      console.log('Selected delivery invoice:', selectedDeliveryInvoice);
      console.log('Selected products:', selectedProducts);
      
      // Validate required fields
      if (!formData.customerId || !formData.projectId || !formData.deliveryInvoiceId) {
        console.error('Missing required fields:', {
          customerId: formData.customerId,
          projectId: formData.projectId,
          deliveryInvoiceId: formData.deliveryInvoiceId
        });
        alert('Please select a delivery invoice with valid customer and project data');
        return;
      }
      
      // Get only the selected products for warranty creation
      const productsForWarranty = getSelectedProductsForWarranty();
      
      // Validate that we have selected products
      if (!productsForWarranty || productsForWarranty.length === 0) {
        console.error('No products selected');
        alert('Please select at least one product using the checkboxes');
        return;
      }
      
      // Create warranties for each selected product
      const warrantyPromises = productsForWarranty.map(product => {
        // Validate each product has required fields
        if (!product.productId || !product.warrantyStartDate) {
          console.error('Product missing required fields:', product);
          return Promise.reject(new Error(`Product ${product.productName} is missing required fields`));
        }
        
        const warrantyData = {
          customerId: formData.customerId,
          projectId: formData.projectId,
          workOrder: selectedDeliveryInvoice?.workOrder || { number: 'WO-001', date: new Date().toISOString() },
          deliveryInvoiceId: formData.deliveryInvoiceId,
          productId: product.productId,
          serviceWarrantyYears: product.serviceWarrantyYears || 0,
          sparePartsWarrantyYears: product.sparePartsWarrantyYears || 0,
          compressorWarrantyYears: product.compressorWarrantyYears || 0,
          warrantyYears: product.warrantyYears || 0,
          warrantyStartDate: product.warrantyStartDate,
        };
        
        console.log('Sending warranty data:', warrantyData);
        
        // Validate required fields
        const requiredFields = ['customerId', 'projectId', 'workOrder', 'deliveryInvoiceId', 'productId', 'warrantyStartDate'];
        const missingFields = requiredFields.filter(field => {
          const value = warrantyData[field as keyof typeof warrantyData];
          return !value || (typeof value === 'string' && value.trim() === '');
        });
        
        if (missingFields.length > 0) {
          console.error('Missing required fields in warranty data:', missingFields);
          return Promise.reject(new Error(`Missing required fields: ${missingFields.join(', ')}`));
        }
        
        return apiService.post('/warranties', warrantyData);
      });

      await Promise.all(warrantyPromises);
      toast.success('Warranties added successfully');
      
      setShowAddModal(false);
      resetForm();
      
      // Immediately clear warranties cache and force refresh
      apiService.invalidateWarrantiesCache();
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchWarranties();
      }, 100);
    } catch (error) {
      console.error('Failed to add warranties:', error);
      toast.error('Failed to add warranties');
    }
  };

  const handleEditWarranty = async () => {
    if (!editingWarranty) return;
    
    try {
      const response = await apiService.put(`/warranties/${editingWarranty._id}`, formData);
      toast.success('Warranty updated successfully');
      
      setShowEditModal(false);
      resetForm();
      
      // Immediately clear warranties cache and force refresh
      apiService.invalidateWarrantiesCache();
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchWarranties();
      }, 100);
    } catch (error) {
      console.error('Failed to update warranty:', error);
      toast.error('Failed to update warranty');
    }
  };

  const handleDeleteWarranty = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this warranty?')) return;
    
    // Double-check admin permissions before attempting delete
    if (!isAdmin) {
      toast.error('Only admin users can delete warranties');
      return;
    }

    try {
      await apiService.delete(`/warranties/${id}`);
      toast.success('Warranty deleted successfully');
      
      // Immediately clear warranties cache and force refresh
      apiService.invalidateWarrantiesCache();
      
      // Force refresh to get updated data with a small delay to ensure cache is cleared
      setTimeout(async () => {
        await fetchWarranties();
      }, 100);
    } catch (error) {
      console.error('Failed to delete warranty:', error);
      toast.error('Failed to delete warranty');
    }
  };

  const resetForm = () => {
    setFormData({
      deliveryInvoiceId: '',
      customerId: '',
      projectId: '',
      workOrder: { number: '', date: '' },
      productId: '',
      serviceWarrantyYears: 0,
      sparePartsWarrantyYears: 0,
      compressorWarrantyYears: 0,
      warrantyYears: 0,
      warrantyStartDate: new Date().toISOString().split('T')[0],
    });
    setSelectedDeliveryInvoice(null);
    setSelectedProducts([]);
    setEditingWarranty(null);
  };

  const openEditModal = (warranty: WarrantyItem) => {
    setEditingWarranty(warranty);
    setFormData({
      deliveryInvoiceId: warranty.deliveryInvoice?._id || '',
      customerId: warranty.customer._id,
      projectId: warranty.project._id,
      workOrder: warranty.workOrder || { number: '', date: '' },
      productId: warranty.product._id,
      serviceWarrantyYears: warranty.serviceWarrantyYears || 0,
      sparePartsWarrantyYears: warranty.sparePartsWarrantyYears || 0,
      compressorWarrantyYears: warranty.compressorWarrantyYears || 0,
      warrantyYears: warranty.warrantyYears || 0,
      warrantyStartDate: warranty.warrantyStartDate.split('T')[0],
    });
    setShowEditModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expiring_soon':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'expiring_soon':
        return 'Expiring Soon';
      case 'expired':
        return 'Expired';
      default:
        return 'Unknown';
    }
  };

  const calculateAccurateRemainingDays = (startDate: string, years: number) => {
    if (!startDate || !years) return 0;
    const start = new Date(startDate);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + years);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Use floor for better 'passed' accuracy
    return diffDays;
  };

  const renderRemainingDays = (days: number, years?: number) => {
    if (years === 0 || years === undefined) {
      return <span className="text-[10px] text-gray-400 italic font-medium">N/A</span>;
    }
    if (days < 0) {
      return (
        <div className="text-sm font-bold mt-1 text-red-600">
          {Math.abs(days)} Days Passed
        </div>
      );
    }
    return (
      <div className={`text-sm font-bold mt-1 ${days <= 30 ? 'text-red-600' : 'text-green-600'}`}>
        {days} Days Remaining
      </div>
    );
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) newSet.delete(groupId);
      else newSet.add(groupId);
      return newSet;
    });
  };

  const filteredWarranties = Array.isArray(warranties) ? warranties.filter(warranty => {
    const matchesSearch = 
      warranty.customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      warranty.project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      warranty.product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (warranty.serialNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      warranty.workOrder.number.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || warranty.status === filterStatus;
    
    const matchesCustomer = !filterCustomerId || (warranty.customer._id === filterCustomerId);
    const matchesProject = !filterProjectId || (warranty.project._id === filterProjectId);
    const matchesInvoiceNumber = !filterInvoiceNumber || (warranty.deliveryInvoice?.invoiceNumber || '').toLowerCase().includes(filterInvoiceNumber.toLowerCase());
    
    const invoiceDateStr = warranty.deliveryInvoice?.date || warranty.deliveryInvoice?.createdAt;
    const formattedInvoiceDate = invoiceDateStr ? new Date(invoiceDateStr).toISOString().split('T')[0] : '';
    const matchesInvoiceDate = !filterInvoiceDate || formattedInvoiceDate === filterInvoiceDate;
    
    return matchesSearch && matchesStatus && matchesCustomer && matchesProject && matchesInvoiceNumber && matchesInvoiceDate;
  }) : [];

  // Group filtered warranties by Customer and Project
  const groupedWarranties = filteredWarranties.reduce((acc: any, warranty) => {
    const groupId = `${warranty.customer._id}-${warranty.project._id}`;
    if (!acc[groupId]) {
      acc[groupId] = {
        id: groupId,
        customer: warranty.customer,
        project: warranty.project,
        items: [],
        latestInvoiceDate: new Date(0)
      };
    }
    acc[groupId].items.push(warranty);
    
    // Track latest invoice date for the group
    const invoiceDate = new Date(warranty.deliveryInvoice?.date || warranty.deliveryInvoice?.createdAt || 0);
    if (invoiceDate > acc[groupId].latestInvoiceDate) {
      acc[groupId].latestInvoiceDate = invoiceDate;
    }
    
    return acc;
  }, {});

  // Convert to array and sort groups by latest invoice date (newest first)
  const sortedGroups = Object.values(groupedWarranties).sort((a: any, b: any) => 
    b.latestInvoiceDate.getTime() - a.latestInvoiceDate.getTime()
  );

  // Sort items within each group by invoice date (newest first)
  sortedGroups.forEach((group: any) => {
    group.items.sort((a: any, b: any) => {
      const dateA = new Date(a.deliveryInvoice?.date || a.deliveryInvoice?.createdAt || 0).getTime();
      const dateB = new Date(b.deliveryInvoice?.date || b.deliveryInvoice?.createdAt || 0).getTime();
      return dateB - dateA;
    });
  });

  const updateSelectedProduct = (index: number, field: string, value: any) => {
    const updatedProducts = [...selectedProducts];
    updatedProducts[index] = { ...updatedProducts[index], [field]: value };
    setSelectedProducts(updatedProducts);
  };

  const handleProductSelection = (productId: string, checked: boolean) => {
    if (checked) {
      setSelectedProductIds(prev => new Set(Array.from(prev).concat(productId)));
    } else {
      setSelectedProductIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const handleSelectAllProducts = () => {
    const allProductIds = selectedProducts.map(product => product.productId);
    setSelectedProductIds(new Set(allProductIds));
  };

  const handleDeselectAllProducts = () => {
    setSelectedProductIds(new Set());
  };

  // Get only the selected products for warranty creation
  const getSelectedProductsForWarranty = () => {
    return selectedProducts.filter(product => selectedProductIds.has(product.productId));
  };



  // Service-related functions
  const fetchServices = async (warrantyId?: string) => {
    try {
      const url = warrantyId ? `/warranties/${warrantyId}/services` : '/warranties/services';
      const response = await apiService.get(url);
      setServices(response.data || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
      toast.error('Failed to load services');
    }
  };

  const handleAddService = async () => {
    try {
      if (!serviceFormData.warrantyId || !serviceFormData.serviceDate || !serviceFormData.serviceType) {
        toast.error('Please fill in all required fields');
        return;
      }

      await apiService.post('/warranties/services', serviceFormData);
      toast.success('Service added successfully');
      
      // Refresh services list
      await fetchServices(serviceFormData.warrantyId);
      
      // Close modal and reset form
      setShowServiceModal(false);
      setServiceFormData({
        warrantyId: '',
        serviceDate: new Date().toISOString().split('T')[0],
        serviceType: 'maintenance',
        workDetails: '',
        performer: ''
      });
      
      // Clear warranties cache and refresh to show updated service counts
      apiService.invalidateWarrantiesCache();
      
      // Refresh service counts and warranties list immediately
      setTimeout(async () => {
        await fetchServiceCounts();
        await fetchWarranties();
      }, 100);
    } catch (error) {
      console.error('Failed to add service:', error);
      toast.error('Failed to add service');
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    try {
      await apiService.delete(`/warranties/services/${serviceId}`);
      toast.success('Service deleted successfully');
      
      // Refresh services list
      await fetchServices(serviceFormData.warrantyId);
      
      // Clear warranties cache and refresh to show updated service counts
      apiService.invalidateWarrantiesCache();
      
      // Refresh service counts and warranties list immediately
      setTimeout(async () => {
        await fetchServiceCounts();
        await fetchWarranties();
      }, 100);
    } catch (error) {
      console.error('Failed to delete service:', error);
      toast.error('Failed to delete service');
    }
  };

  const openServiceModal = (warranty: WarrantyItem) => {
    setSelectedWarrantyForService(warranty);
    setServiceFormData({
      warrantyId: warranty._id,
      serviceDate: new Date().toISOString().split('T')[0],
      serviceType: 'maintenance',
      workDetails: '',
      performer: ''
    });
    setShowServiceModal(true);    
    // Fetch existing services for this warranty
    fetchServices(warranty._id);
  };

  const resetServiceForm = () => {
    setServiceFormData({
      warrantyId: '',
      serviceDate: new Date().toISOString().split('T')[0],
      serviceType: 'maintenance',
      workDetails: '',
      performer: ''
    });
  };

  // Open warranty view modal
  const openViewModal = (warranty: WarrantyItem) => {
    setSelectedWarrantyForView(warranty);
    setShowViewModal(true);
  };

  // Open service details modal
  const openServiceDetailsModal = async (warranty: WarrantyItem) => {
    setSelectedWarrantyForDetails(warranty);
    setShowServiceDetailsModal(true);
    
    // Fetch services for this warranty
    try {
      const response = await apiService.get(`/warranties/${warranty._id}/services`);
      setServices(response.data || []);
      
      // Also refresh service counts to ensure accuracy
      await fetchServiceCounts();
    } catch (error) {
      console.error('Error fetching warranty services:', error);
      toast.error('Failed to load service details');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Warranty Management</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => { 
              apiService.invalidateWarrantiesCache(); 
              fetchWarranties(); 
            }}
            title="Refresh"
            className="flex items-center justify-center p-2 rounded-lg hover:bg-gray-50 transition-colors bg-transparent"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {isAdmin && (
            <Button onClick={() => setShowAddModal(true)}>
              + Add Warranty
            </Button>
          )}
        </div>
      </div>
      


      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          <div className="font-bold mb-2">⚠️ Backend Not Ready</div>
          <div className="text-sm">
            The warranty management system is being deployed. Please try again in a few minutes.
            <br />
            Error: {error}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg shadow-sm">
          <div className="font-bold mb-1 flex items-center gap-2">
            <span>⚠️</span> Backend Not Ready
          </div>
          <div className="text-xs opacity-90">
            The warranty management system is being deployed. Please try again in a few minutes.
            <br />
            Error: {error}
          </div>
        </div>
      )}

      {/* Modern Filter Row */}
      <div className="bg-[var(--color-surface)] p-4 rounded-xl shadow-sm border border-[var(--color-border)] mb-6 text-[var(--color-text)]">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]" size={16} />
              <Input
                placeholder="Keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 h-[42px] rounded-lg"
              />
            </div>
          </div>
          
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1">Status</label>
            <SearchableSelect
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="All Status"
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1 flex items-center gap-1">
              <User size={10} /> Customer
            </label>
            <SearchableSelect
              options={customerOptions}
              value={filterCustomerId}
              onChange={(val) => {
                setFilterCustomerId(val);
                setFilterProjectId('');
              }}
              placeholder="Select Customer"
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1 flex items-center gap-1">
              <Briefcase size={10} /> Project
            </label>
            <SearchableSelect
              options={projectOptions}
              value={filterProjectId}
              onChange={setFilterProjectId}
              placeholder={filterCustomerId ? "All Projects" : "Select Customer First"}
              disabled={!filterCustomerId}
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1 flex items-center gap-1">
              <FileText size={10} /> Invoice No
            </label>
            <SearchableSelect
              options={invoiceOptions}
              value={filterInvoiceNumber}
              onChange={setFilterInvoiceNumber}
              placeholder="Invoice No"
              className="w-full"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider ml-1 flex items-center gap-1">
              <Calendar size={10} /> Date
            </label>
            <Input
              type="date"
              value={filterInvoiceDate}
              onChange={(e) => setFilterInvoiceDate(e.target.value)}
              className="w-full h-[42px] rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      {/* Grouped Warranty List */}
      <div className="space-y-4">
        {sortedGroups.length === 0 ? (
          <div className="bg-[var(--color-surface)] p-12 rounded-2xl border border-dashed border-[var(--color-border)] text-center shadow-sm text-[var(--color-text)]">
            <div className="bg-[var(--color-background)] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="text-[var(--color-text-secondary)]" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-[var(--color-text)]">No warranties found</h3>
            <p className="text-[var(--color-text-secondary)] mt-1 max-w-xs mx-auto">Try adjusting your filters or search terms to find what you're looking for.</p>
            {isAdmin && (
              <Button onClick={() => setShowAddModal(true)} className="mt-6">
                Add Your First Warranty
              </Button>
            )}
          </div>
        ) : (
          sortedGroups.map((group: any) => (
            <div key={group.id} className="bg-[var(--color-surface)] rounded-xl shadow-sm border border-[var(--color-border)] overflow-hidden transition-all duration-200 hover:shadow-md">
              {/* Group Header */}
              <div 
                className={`p-4 flex items-center justify-between cursor-pointer transition-colors ${
                  expandedGroups.has(group.id) ? 'bg-blue-50/50 dark:bg-blue-950/30 border-b border-[var(--color-border)]' : 'hover:bg-[var(--color-surface-hover)]'
                }`}
                onClick={() => toggleGroup(group.id)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                    expandedGroups.has(group.id) ? 'bg-blue-600 text-white' : 'bg-[var(--color-background)] text-[var(--color-text-secondary)]'
                  }`}>
                    {expandedGroups.has(group.id) ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[var(--color-text)] flex items-center gap-2">
                      <User size={14} className="text-[var(--color-text-secondary)]" />
                      {group.customer.name}
                    </h3>
                    <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold flex items-center gap-2 mt-0.5">
                      <Briefcase size={12} className="text-blue-400" />
                      {group.project.name}
                    </div>
                  </div>
                  {(() => {
                    const hasActive = group.items.some((it: any) => {
                      const days = calculateAccurateRemainingDays(it.warrantyStartDate, it.warrantyYears || 0);
                      const sDays = calculateAccurateRemainingDays(it.warrantyStartDate, it.serviceWarrantyYears || 0);
                      const pDays = calculateAccurateRemainingDays(it.warrantyStartDate, it.sparePartsWarrantyYears || 0);
                      const cDays = calculateAccurateRemainingDays(it.warrantyStartDate, it.compressorWarrantyYears || 0);
                      return days > 0 || sDays > 0 || pDays > 0 || cDays > 0;
                    });
                    return (
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter ${
                        hasActive ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-800' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border border-red-200 dark:border-red-800'
                      }`}>
                        {hasActive ? 'Group Active' : 'Group Expired'}
                      </span>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right hidden sm:block">
                    <div className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-widest">Latest Activity</div>
                    <div className="text-xs font-medium text-[var(--color-text)]">{formatDateTime(group.latestInvoiceDate, settings)}</div>
                  </div>
                  <div className="bg-[var(--color-surface)] px-3 py-1.5 rounded-lg border border-[var(--color-border)] shadow-sm flex items-center gap-2">
                    <span className="text-xs font-bold text-[var(--color-text)]">{group.items.length}</span>
                    <span className="text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">
                      {group.items.length === 1 ? 'Warranty' : 'Warranties'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Group Content (Table) */}
              {expandedGroups.has(group.id) && (
                <DoubleScrollbar>
                  <table className="min-w-full divide-y divide-[var(--color-border)]">
                    <thead className="bg-[var(--color-background)]">
                      <tr>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Product Info</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Invoice Details</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Service Warranty</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Spare Parts</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Compressor</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">General</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-center">Status</th>
                        <th className="px-6 py-3 text-right text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)]">
                      {group.items.map((warranty: WarrantyItem) => (
                        <tr key={warranty._id} className="hover:bg-[var(--color-background-hover)] transition-colors">
                          <td className="px-6 py-4">
                            <div className="min-w-[250px] space-y-1">
                              <div className="font-bold text-[var(--color-text)] text-sm whitespace-normal break-words">{warranty.product.name}</div>
                              <div className="text-[11px] text-[var(--color-text-secondary)] font-medium whitespace-normal break-words flex flex-col gap-0.5">
                                <div>
                                  <span className="font-bold text-[var(--color-text-secondary)] opacity-75 uppercase text-[9px] mr-1">Brand:</span>
                                  {warranty.brand || warranty.product.customFields?.brand || '-'}
                                </div>
                                <div>
                                  <span className="font-bold text-[var(--color-text-secondary)] opacity-75 uppercase text-[9px] mr-1">Model:</span>
                                  {warranty.model || warranty.product.customFields?.model || '-'}
                                </div>
                                {warranty.serialNo && (
                                  <div className="text-blue-600 dark:text-blue-400 font-bold">
                                    <span className="font-bold text-[var(--color-text-secondary)] opacity-75 uppercase text-[9px] mr-1">Serial:</span>
                                    {warranty.serialNo}
                                  </div>
                                )}
                                {(warranty.compressorType || warranty.product.customFields?.compressorType) && (
                                  <div>
                                    <span className="font-bold text-[var(--color-text-secondary)] opacity-75 uppercase text-[9px] mr-1">Compressor:</span>
                                    {warranty.compressorType || warranty.product.customFields?.compressorType}
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-1">
                                <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-[var(--color-background)] border border-[var(--color-border)] text-[10px] font-bold text-[var(--color-text-secondary)]">
                                  WO: {warranty.workOrder.number}
                                </div>
                                <div className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/40 text-[10px] font-bold text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                  Qty: {warranty.quantity ?? 0} {warranty.unit || warranty.product?.customFields?.unit || 'pcs'}
                                </div>
                              </div>
                              {warranty.notes && (
                                <div className="text-[10px] text-[var(--color-text-secondary)] italic mt-1 border-l-2 border-[var(--color-border)] pl-2 whitespace-normal break-words">
                                  Note: {warranty.notes}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <span className="text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md inline-block self-start border border-blue-200 dark:border-blue-800">
                                #{warranty.deliveryInvoice?.invoiceNumber}
                              </span>
                              <div className="text-[11px] text-[var(--color-text-secondary)] flex items-center gap-1 ml-1">
                                <Calendar size={10} />
                                {(() => {
                                  const dateValue = warranty.deliveryInvoice?.deliveryDate
                                    || warranty.deliveryInvoice?.date
                                    || warranty.deliveryInvoice?.createdAt;
                                  return dateValue ? formatDateTime(dateValue, settings) : '-';
                                })()}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-tight">{warranty.serviceWarrantyYears || 0} Years</span>
                              {renderRemainingDays(calculateAccurateRemainingDays(warranty.warrantyStartDate, warranty.serviceWarrantyYears || 0), warranty.serviceWarrantyYears)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-tight">{warranty.sparePartsWarrantyYears || 0} Years</span>
                              {renderRemainingDays(calculateAccurateRemainingDays(warranty.warrantyStartDate, warranty.sparePartsWarrantyYears || 0), warranty.sparePartsWarrantyYears)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-tight">{warranty.compressorWarrantyYears || 0} Years</span>
                              {renderRemainingDays(calculateAccurateRemainingDays(warranty.warrantyStartDate, warranty.compressorWarrantyYears || 0), warranty.compressorWarrantyYears)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-[11px] font-bold text-[var(--color-text-secondary)] uppercase tracking-tight">{warranty.warrantyYears || 0} Years</span>
                              {renderRemainingDays(calculateAccurateRemainingDays(warranty.warrantyStartDate, warranty.warrantyYears || 0), warranty.warrantyYears)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider ${getStatusColor(warranty.status)}`}>
                              {getStatusText(warranty.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex justify-end gap-1.5">
                              <button onClick={() => openViewModal(warranty)} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-[var(--color-surface-hover)] rounded-lg transition-colors border border-transparent hover:border-[var(--color-border)]" title="View Detail"><Eye size={16} /></button>
                              <button onClick={() => openServiceModal(warranty)} className="p-2 text-amber-600 dark:text-amber-400 hover:bg-[var(--color-surface-hover)] rounded-lg transition-colors border border-transparent hover:border-[var(--color-border)]" title="Provide Service"><Wrench size={16} /></button>
                              <div className="flex items-center gap-1 ml-2 pl-2 border-l border-[var(--color-border)]">
                                <div className="text-right mr-2 hidden md:block">
                                  <div className="text-[9px] font-bold text-[var(--color-text-secondary)] uppercase">Services</div>
                                  <div className="text-xs font-black text-[var(--color-text)] leading-none">{serviceCounts[warranty._id] || 0}</div>
                                </div>
                                <button
                                  onClick={() => openServiceDetailsModal(warranty)}
                                  className="p-2 text-purple-600 dark:text-purple-400 hover:bg-[var(--color-surface-hover)] rounded-lg transition-colors"
                                  title="Service History"
                                >
                                  <FileText size={16} />
                                </button>
                              </div>
                              {isAdmin && (
                                <div className="flex items-center gap-1 ml-2 pl-2 border-l border-[var(--color-border)]">
                                  <button onClick={() => openEditModal(warranty)} className="p-2 text-green-600 dark:text-green-400 hover:bg-[var(--color-surface-hover)] rounded-lg transition-colors" title="Edit"><Edit size={16} /></button>
                                  <button onClick={() => handleDeleteWarranty(warranty._id)} className="p-2 text-red-600 dark:text-red-400 hover:bg-[var(--color-surface-hover)] rounded-lg transition-colors" title="Delete"><Trash2 size={16} /></button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </DoubleScrollbar>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Warranty Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Warranty"
        className="max-w-4xl" // Increase modal width
      >
        <div className="space-y-4">
          {/* Delivery Invoice Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Delivery Invoice</label>
            <select
              value={formData.deliveryInvoiceId}
              onChange={(e) => handleDeliveryInvoiceChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Delivery Invoice</option>
              {Array.isArray(deliveryInvoices) && deliveryInvoices.map(invoice => (
                <option key={invoice._id} value={invoice._id}>
                  {invoice.invoiceNumber} - {formatDateTime(invoice.date || invoice.createdAt, settings)} | {invoice.customer.name} | {invoice.project?.name || 'No Project'}
                </option>
              ))}
            </select>
          </div>

          {/* Auto-populated fields (read-only) */}
          {selectedDeliveryInvoice && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Customer</label>
                <Input
                  value={selectedDeliveryInvoice.customer.name}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Project</label>
                <Input
                  value={selectedDeliveryInvoice.project?.name || ''}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Work Order</label>
                <Input
                  value={selectedDeliveryInvoice.workOrder?.number || ''}
                  disabled
                  className="bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Work Order Date</label>
                <Input
                  value={selectedDeliveryInvoice.workOrder?.date ? formatDateTime(selectedDeliveryInvoice.workOrder.date, settings) : ''}
                  disabled
                  className="bg-gray-50"
                />
              </div>
            </>
          )}

          {/* Products from Delivery Invoice */}
          {selectedProducts.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium">Products from Delivery Invoice</label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSelectAllProducts}
                    className="text-xs"
                  >
                    Select All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDeselectAllProducts}
                    className="text-xs"
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {selectedProducts.map((product, index) => (
                  <div key={index} className={`p-3 border rounded-md ${selectedProductIds.has(product.productId) ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedProductIds.has(product.productId)}
                        onChange={(e) => handleProductSelection(product.productId, e.target.checked)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium">{product.productName}</span>
                          <span className="text-sm text-gray-500">Qty: {product.quantity}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Service Warranty (Years)</label>
                            <Input
                              type="number"
                              value={product.serviceWarrantyYears}
                              onChange={(e) => updateSelectedProduct(index, 'serviceWarrantyYears', parseInt(e.target.value))}
                              min="0"
                              max="20"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Spare Parts Warranty (Years)</label>
                            <Input
                              type="number"
                              value={product.sparePartsWarrantyYears}
                              onChange={(e) => updateSelectedProduct(index, 'sparePartsWarrantyYears', parseInt(e.target.value))}
                              min="0"
                              max="20"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Compressor Warranty (Years)</label>
                            <Input
                              type="number"
                              value={product.compressorWarrantyYears || 0}
                              onChange={(e) => updateSelectedProduct(index, 'compressorWarrantyYears', parseInt(e.target.value))}
                              min="0"
                              max="20"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Warranty Period (Years)</label>
                            <Input
                              type="number"
                              value={product.warrantyYears}
                              onChange={(e) => updateSelectedProduct(index, 'warrantyYears', parseInt(e.target.value))}
                              min="0"
                              max="20"
                              className="text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                            <Input
                              type="date"
                              value={product.warrantyStartDate}
                              onChange={(e) => updateSelectedProduct(index, 'warrantyStartDate', e.target.value)}
                              className="text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button 
              onClick={handleAddWarranty} 
              className="flex-1"
              disabled={getSelectedProductsForWarranty().length === 0}
            >
              Add Warranties ({getSelectedProductsForWarranty().length})
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Warranty Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Warranty Details"
        className="max-w-4xl"
      >
        {selectedWarrantyForView && (
          <div className="space-y-6 text-[var(--color-text)]">
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-[var(--color-background)] p-4 rounded-lg border border-[var(--color-border)]">
                <h3 className="font-bold text-[var(--color-text)] mb-3 border-b pb-1 border-[var(--color-border)]">Customer & Project</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium text-[var(--color-text-secondary)]">Customer:</span> {selectedWarrantyForView.customer.name}</div>
                  <div><span className="font-medium text-[var(--color-text-secondary)]">Email:</span> {selectedWarrantyForView.customer.email || 'N/A'}</div>
                  <div><span className="font-medium text-[var(--color-text-secondary)]">Phone:</span> {selectedWarrantyForView.customer.phone || 'N/A'}</div>
                  <div className="pt-2"><span className="font-medium text-[var(--color-text-secondary)]">Project:</span> {selectedWarrantyForView.project.name}</div>
                  <div><span className="font-medium text-[var(--color-text-secondary)]">Address:</span> {selectedWarrantyForView.project.address || 'N/A'}</div>
                </div>
              </div>

              <div className="bg-[var(--color-background)] p-4 rounded-lg border border-[var(--color-border)]">
                <h3 className="font-bold text-[var(--color-text)] mb-3 border-b pb-1 border-[var(--color-border)]">Product & Delivery</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium text-[var(--color-text-secondary)]">Product:</span> {selectedWarrantyForView.product.name}</div>
                  <div><span className="font-medium text-[var(--color-text-secondary)]">Brand:</span> {selectedWarrantyForView.brand || selectedWarrantyForView.product.customFields?.brand || 'N/A'}</div>
                  <div><span className="font-medium text-[var(--color-text-secondary)]">Model:</span> {selectedWarrantyForView.model || selectedWarrantyForView.product.customFields?.model || 'N/A'}</div>
                  {(selectedWarrantyForView.compressorType || selectedWarrantyForView.product.customFields?.compressorType) && (
                    <div><span className="font-medium text-[var(--color-text-secondary)]">Compressor Type:</span> {selectedWarrantyForView.compressorType || selectedWarrantyForView.product.customFields?.compressorType}</div>
                  )}
                  <div><span className="font-medium text-[var(--color-text-secondary)]">SKU:</span> {selectedWarrantyForView.product.sku || 'N/A'}</div>
                  <div className="flex gap-4">
                    <div><span className="font-medium text-[var(--color-text-secondary)]">Quantity:</span> {selectedWarrantyForView.quantity ?? 0} {selectedWarrantyForView.unit || selectedWarrantyForView.product?.customFields?.unit || 'pcs'}</div>
                  </div>
                  {selectedWarrantyForView.notes && <div><span className="font-medium text-[var(--color-text-secondary)]">Notes:</span> {selectedWarrantyForView.notes}</div>}
                  <div className="pt-2 border-t border-[var(--color-border)] mt-2">
                    <div><span className="font-medium text-[var(--color-text-secondary)]">Delivery Invoice:</span> {selectedWarrantyForView.deliveryInvoice?.invoiceNumber || 'N/A'}</div>
                    <div><span className="font-medium text-[var(--color-text-secondary)]">Work Order:</span> {selectedWarrantyForView.workOrder.number || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg overflow-hidden">
              <div className="bg-[var(--color-background)] px-4 py-2 border-b border-[var(--color-border)]">
                <h3 className="font-bold text-[var(--color-text)]">Warranty Periods</h3>
              </div>
              <div className="p-0">
                <table className="min-w-full divide-y divide-[var(--color-border)]">
                  <thead className="bg-[var(--color-background)]">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase">Duration</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase">Period</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase">Days Left</th>
                    </tr>
                  </thead>
                  <tbody className="bg-[var(--color-surface)] divide-y divide-[var(--color-border)] text-sm">
                    <tr>
                      <td className="px-4 py-3 font-medium">Service Warranty</td>
                      <td className="px-4 py-3">{selectedWarrantyForView.serviceWarrantyYears || 0} {selectedWarrantyForView.serviceWarrantyYears === 1 ? 'Year' : 'Years'}</td>
                      <td className="px-4 py-3">
                        {selectedWarrantyForView.serviceWarrantyYears ? (
                          `${formatDateTime(selectedWarrantyForView.warrantyStartDate, settings)} - ${formatDateTime(selectedWarrantyForView.serviceWarrantyEndDate!, settings)}`
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        {selectedWarrantyForView.serviceWarrantyYears ? (() => {
                          const days = calculateAccurateRemainingDays(selectedWarrantyForView.warrantyStartDate, selectedWarrantyForView.serviceWarrantyYears);
                          return (
                            <span className={`font-bold ${days <= 30 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              {days < 0 ? `${Math.abs(days)} Days Passed` : `${days} Days Left`}
                            </span>
                          );
                        })() : <span className="text-[var(--color-text-secondary)] italic">N/A</span>}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Spare Parts Warranty</td>
                      <td className="px-4 py-3">{selectedWarrantyForView.sparePartsWarrantyYears || 0} {selectedWarrantyForView.sparePartsWarrantyYears === 1 ? 'Year' : 'Years'}</td>
                      <td className="px-4 py-3">
                        {selectedWarrantyForView.sparePartsWarrantyYears ? (
                          `${formatDateTime(selectedWarrantyForView.warrantyStartDate, settings)} - ${formatDateTime(selectedWarrantyForView.sparePartsWarrantyEndDate!, settings)}`
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        {selectedWarrantyForView.sparePartsWarrantyYears ? (() => {
                          const days = calculateAccurateRemainingDays(selectedWarrantyForView.warrantyStartDate, selectedWarrantyForView.sparePartsWarrantyYears);
                          return (
                            <span className={`font-bold ${days <= 30 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              {days < 0 ? `${Math.abs(days)} Days Passed` : `${days} Days Left`}
                            </span>
                          );
                        })() : <span className="text-[var(--color-text-secondary)] italic">N/A</span>}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">Compressor Warranty</td>
                      <td className="px-4 py-3">{selectedWarrantyForView.compressorWarrantyYears || 0} {selectedWarrantyForView.compressorWarrantyYears === 1 ? 'Year' : 'Years'}</td>
                      <td className="px-4 py-3">
                        {selectedWarrantyForView.compressorWarrantyYears ? (
                          `${formatDateTime(selectedWarrantyForView.warrantyStartDate, settings)} - ${formatDateTime(selectedWarrantyForView.compressorWarrantyEndDate!, settings)}`
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        {selectedWarrantyForView.compressorWarrantyYears ? (() => {
                          const days = calculateAccurateRemainingDays(selectedWarrantyForView.warrantyStartDate, selectedWarrantyForView.compressorWarrantyYears);
                          return (
                            <span className={`font-bold ${days <= 30 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              {days < 0 ? `${Math.abs(days)} Days Passed` : `${days} Days Left`}
                            </span>
                          );
                        })() : <span className="text-[var(--color-text-secondary)] italic">N/A</span>}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium">General Warranty</td>
                      <td className="px-4 py-3">{selectedWarrantyForView.warrantyYears || 0} {selectedWarrantyForView.warrantyYears === 1 ? 'Year' : 'Years'}</td>
                      <td className="px-4 py-3">
                        {selectedWarrantyForView.warrantyYears ? (
                          `${formatDateTime(selectedWarrantyForView.warrantyStartDate, settings)} - ${formatDateTime(selectedWarrantyForView.warrantyEndDate!, settings)}`
                        ) : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        {selectedWarrantyForView.warrantyYears ? (() => {
                          const days = calculateAccurateRemainingDays(selectedWarrantyForView.warrantyStartDate, selectedWarrantyForView.warrantyYears);
                          return (
                            <span className={`font-bold ${days <= 30 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                              {days < 0 ? `${Math.abs(days)} Days Passed` : `${days} Days Left`}
                            </span>
                          );
                        })() : <span className="text-[var(--color-text-secondary)] italic">N/A</span>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-[var(--color-border)]">
              <Button
                variant="outline"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </Button>
              {isAdmin && (
                <Button
                  onClick={() => {
                    setShowViewModal(false);
                    openEditModal(selectedWarrantyForView);
                  }}
                >
                  Edit Warranty
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Warranty Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Warranty"
      >
        <div className="space-y-4 text-[var(--color-text)]">
          {/* Read-only fields */}
          {editingWarranty && (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">Customer</label>
                <Input
                  value={editingWarranty.customer.name}
                  disabled
                  className="bg-[var(--color-background)] opacity-80"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Project</label>
                <Input
                  value={editingWarranty.project.name}
                  disabled
                  className="bg-[var(--color-background)] opacity-80"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Work Order</label>
                <Input
                  value={editingWarranty.workOrder.number}
                  disabled
                  className="bg-[var(--color-background)] opacity-80"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Product</label>
                <Input
                  value={`${editingWarranty.product.name} - ${editingWarranty.product.brand || ''} ${editingWarranty.product.model || ''}`}
                  disabled
                  className="bg-[var(--color-background)] opacity-80"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Delivery Invoice</label>
                <Input
                  value={editingWarranty.deliveryInvoice?.invoiceNumber || 'N/A'}
                  disabled
                  className="bg-[var(--color-background)] opacity-80"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Current Status</label>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(editingWarranty.status)}`}>
                    {getStatusText(editingWarranty.status)}
                  </span>
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    {editingWarranty.warrantyYears ? (() => {
                      const days = calculateAccurateRemainingDays(editingWarranty.warrantyStartDate, editingWarranty.warrantyYears);
                      return `(${days < 0 ? `${Math.abs(days)} days passed` : `${days} days remaining`})`;
                    })() : '(N/A)'}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Editable fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Service Warranty (Years)</label>
              <Input
                type="number"
                value={formData.serviceWarrantyYears}
                onChange={(e) => setFormData({ ...formData, serviceWarrantyYears: parseInt(e.target.value) })}
                min="0"
                max="20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Spare Parts Warranty (Years)</label>
              <Input
                type="number"
                value={formData.sparePartsWarrantyYears}
                onChange={(e) => setFormData({ ...formData, sparePartsWarrantyYears: parseInt(e.target.value) })}
                min="0"
                max="20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Compressor Warranty (Years)</label>
              <Input
                type="number"
                value={formData.compressorWarrantyYears || 0}
                onChange={(e) => setFormData({ ...formData, compressorWarrantyYears: parseInt(e.target.value) })}
                min="0"
                max="20"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Warranty Period (Years)</label>
              <Input
                type="number"
                value={formData.warrantyYears}
                onChange={(e) => setFormData({ ...formData, warrantyYears: parseInt(e.target.value) })}
                min="0"
                max="20"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Warranty Start Date</label>
            <Input
              type="date"
              value={formData.warrantyStartDate}
              onChange={(e) => setFormData({ ...formData, warrantyStartDate: e.target.value })}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleEditWarranty} className="flex-1">
              Update Warranty
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowEditModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Service Modal */}
      <Modal
        isOpen={showServiceModal}
        onClose={() => setShowServiceModal(false)}
        title="Provide Service"
        className="max-w-4xl"
      >
        <div className="space-y-4 text-[var(--color-text)]">
          {/* Warranty Information */}
          {selectedWarrantyForService && (
            <div className="bg-[var(--color-background)] p-4 rounded-lg mb-4 border border-[var(--color-border)]">
              <h3 className="font-medium text-[var(--color-text)] mb-2">Warranty Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Customer:</span> {selectedWarrantyForService.customer.name}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Product:</span> {selectedWarrantyForService.product.name}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Brand:</span> {selectedWarrantyForService.brand || selectedWarrantyForService.product.customFields?.brand || 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Model:</span> {selectedWarrantyForService.model || selectedWarrantyForService.product.customFields?.model || 'N/A'}
                </div>
                {(selectedWarrantyForService.compressorType || selectedWarrantyForService.product.customFields?.compressorType) && (
                  <div>
                    <span className="font-medium text-[var(--color-text-secondary)]">Compressor:</span> {selectedWarrantyForService.compressorType || selectedWarrantyForService.product.customFields?.compressorType}
                  </div>
                )}
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Qty:</span> {selectedWarrantyForService.quantity ?? 0} {selectedWarrantyForService.unit || selectedWarrantyForService.product?.customFields?.unit || 'pcs'}
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-[var(--color-text-secondary)]">Project:</span> {selectedWarrantyForService.project.name}
                </div>
                {selectedWarrantyForService.notes && (
                  <div className="col-span-2 text-[var(--color-text-secondary)] italic">
                    <span className="font-medium text-[var(--color-text)] not-italic">Notes:</span> {selectedWarrantyForService.notes}
                  </div>
                )}
                <div className="border-t border-[var(--color-border)] col-span-2 my-1 pt-2"></div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Service Warranty:</span> {selectedWarrantyForService.serviceWarrantyYears ? (() => {
                    const days = calculateAccurateRemainingDays(selectedWarrantyForService.warrantyStartDate, selectedWarrantyForService.serviceWarrantyYears);
                    return days < 0 ? `${Math.abs(days)} days passed` : `${days} days remaining`;
                  })() : 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Spare Parts Warranty:</span> {selectedWarrantyForService.sparePartsWarrantyYears ? (() => {
                    const days = calculateAccurateRemainingDays(selectedWarrantyForService.warrantyStartDate, selectedWarrantyForService.sparePartsWarrantyYears);
                    return days < 0 ? `${Math.abs(days)} days passed` : `${days} days remaining`;
                  })() : 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Compressor Warranty:</span> {selectedWarrantyForService.compressorWarrantyYears ? (() => {
                    const days = calculateAccurateRemainingDays(selectedWarrantyForService.warrantyStartDate, selectedWarrantyForService.compressorWarrantyYears);
                    return days < 0 ? `${Math.abs(days)} days passed` : `${days} days remaining`;
                  })() : 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">General Warranty:</span> {selectedWarrantyForService.warrantyYears ? (() => {
                    const days = calculateAccurateRemainingDays(selectedWarrantyForService.warrantyStartDate, selectedWarrantyForService.warrantyYears);
                    return days < 0 ? `${Math.abs(days)} days passed` : `${days} days remaining`;
                  })() : 'N/A'}
                </div>
              </div>
            </div>
          )}

          {/* Service Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Service Date *</label>
              <Input
                type="date"
                value={serviceFormData.serviceDate}
                onChange={(e) => setServiceFormData({ ...serviceFormData, serviceDate: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Service Type *</label>
              <select
                value={serviceFormData.serviceType}
                onChange={(e) => setServiceFormData({ ...serviceFormData, serviceType: e.target.value as any })}
                className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="maintenance">Maintenance</option>
                <option value="repair">Repair</option>
                <option value="inspection">Inspection</option>
                <option value="emergency">Emergency</option>
                <option value="regular_servicing">Regular Servicing</option>
                <option value="master_servicing">Master Servicing</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Service Provider (Employee)</label>
              <SearchableSelect
                options={employeeOptions}
                value={serviceFormData.performer}
                onChange={(value) => setServiceFormData({ ...serviceFormData, performer: value })}
                placeholder="Select an employee..."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Work Details *</label>
            <textarea
              value={serviceFormData.workDetails}
              onChange={(e) => setServiceFormData({ ...serviceFormData, workDetails: e.target.value })}
              placeholder="Describe the work performed..."
              className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={4}
            />
          </div>

          {/* Existing Services */}
          {services.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium text-[var(--color-text)] mb-3">Previous Services</h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {services.map((service) => (
                  <div key={service._id} className="bg-[var(--color-background)] p-3 rounded border border-[var(--color-border)] text-[var(--color-text)]">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">{formatDateTime(service.serviceDate, settings)}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            service.serviceType === 'emergency' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                            service.serviceType === 'repair' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' :
                            service.serviceType === 'maintenance' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                            service.serviceType === 'inspection' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                            'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
                          }`}>
                            {service.serviceType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                          </span>
                        </div>
                        <div className="text-[11px] text-[var(--color-text-secondary)] mb-1">
                          <span className="font-bold">Provider:</span> {service.performer?.name || 'N/A'}
                        </div>
                        <div className="text-sm text-[var(--color-text)] whitespace-pre-wrap">{service.workDetails}</div>
                      </div>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteService(service._id)}
                          className="p-1 ml-2"
                          title="Delete Service"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button onClick={handleAddService} className="flex-1">
              Add Service
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowServiceModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Service Details Modal */}
      <Modal
        isOpen={showServiceDetailsModal}
        onClose={() => setShowServiceDetailsModal(false)}
        title="Service Warranty Details"
        className="max-w-4xl"
      >
        <div className="space-y-4 text-[var(--color-text)]">
          {/* Warranty Information */}
          {selectedWarrantyForDetails && (
            <div className="bg-[var(--color-background)] p-4 rounded-lg mb-4 border border-[var(--color-border)]">
              <h3 className="font-medium text-[var(--color-text)] mb-2">Warranty Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Customer:</span> {selectedWarrantyForDetails.customer.name}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Product:</span> {selectedWarrantyForDetails.product.name}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Brand:</span> {selectedWarrantyForDetails.brand || selectedWarrantyForDetails.product.customFields?.brand || 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Model:</span> {selectedWarrantyForDetails.model || selectedWarrantyForDetails.product.customFields?.model || 'N/A'}
                </div>
                {(selectedWarrantyForDetails.compressorType || selectedWarrantyForDetails.product.customFields?.compressorType) && (
                  <div>
                    <span className="font-medium text-[var(--color-text-secondary)]">Compressor:</span> {selectedWarrantyForDetails.compressorType || selectedWarrantyForDetails.product.customFields?.compressorType}
                  </div>
                )}
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Qty:</span> {selectedWarrantyForDetails.quantity ?? 0} {selectedWarrantyForDetails.unit || selectedWarrantyForDetails.product?.customFields?.unit || 'pcs'}
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-[var(--color-text-secondary)]">Project:</span> {selectedWarrantyForDetails.project.name}
                </div>
                {selectedWarrantyForDetails.notes && (
                  <div className="col-span-2 text-[var(--color-text-secondary)] italic">
                    <span className="font-medium text-[var(--color-text)] not-italic">Notes:</span> {selectedWarrantyForDetails.notes}
                  </div>
                )}
                <div className="border-t border-[var(--color-border)] col-span-2 my-1 pt-2"></div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Service Warranty:</span> {selectedWarrantyForDetails.serviceWarrantyYears ? (() => {
                    const days = calculateAccurateRemainingDays(selectedWarrantyForDetails.warrantyStartDate, selectedWarrantyForDetails.serviceWarrantyYears);
                    return days < 0 ? `${Math.abs(days)} days passed` : `${days} days remaining`;
                  })() : 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Spare Parts Warranty:</span> {selectedWarrantyForDetails.sparePartsWarrantyYears ? (() => {
                    const days = calculateAccurateRemainingDays(selectedWarrantyForDetails.warrantyStartDate, selectedWarrantyForDetails.sparePartsWarrantyYears);
                    return days < 0 ? `${Math.abs(days)} days passed` : `${days} days remaining`;
                  })() : 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">Compressor Warranty:</span> {selectedWarrantyForDetails.compressorWarrantyYears ? (() => {
                    const days = calculateAccurateRemainingDays(selectedWarrantyForService?.warrantyStartDate || selectedWarrantyForDetails.warrantyStartDate, selectedWarrantyForDetails.compressorWarrantyYears);
                    return days < 0 ? `${Math.abs(days)} days passed` : `${days} days remaining`;
                  })() : 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-[var(--color-text-secondary)]">General Warranty:</span> {selectedWarrantyForDetails.warrantyYears ? (() => {
                    const days = calculateAccurateRemainingDays(selectedWarrantyForDetails.warrantyStartDate, selectedWarrantyForDetails.warrantyYears);
                    return days < 0 ? `${Math.abs(days)} days passed` : `${days} days remaining`;
                  })() : 'N/A'}
                </div>
              </div>
            </div>
          )}

          {/* Services List */}
          {services.length > 0 ? (
            <div className="space-y-3">
              <h3 className="font-medium text-[var(--color-text)]">Service History</h3>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {services.map((service) => (
                  <div key={service._id} className="bg-[var(--color-background)] p-3 rounded border border-[var(--color-border)] text-[var(--color-text)]">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">{formatDateTime(service.serviceDate, settings)}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            service.serviceType === 'emergency' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                            service.serviceType === 'repair' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' :
                            service.serviceType === 'maintenance' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' :
                            service.serviceType === 'inspection' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' :
                            service.serviceType === 'regular_servicing' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' :
                            service.serviceType === 'master_servicing' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300' :
                            'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
                          }`}>
                            {service.serviceType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                        <div className="text-[11px] text-[var(--color-text-secondary)] mb-1">
                          <span className="font-bold">Provider:</span> {service.performer?.name || 'N/A'}
                        </div>
                        {service.workDetails && (
                          <div className="text-sm text-[var(--color-text)] mt-1 whitespace-pre-wrap">{service.workDetails}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-[var(--color-text-secondary)] text-lg mb-2">No services found</div>
              <div className="text-[var(--color-text-secondary)] opacity-70 text-sm">No services have been provided for this warranty yet.</div>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowServiceDetailsModal(false)}
              className="flex-1"
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WarrantyPage; 